import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { useIsEInvoicingOnly } from '@features/bills/hooks/useIsEInvoicingOnly'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { EInvoiceListFilter } from '../../eInvoicing/constants/eInvoiceListFilter'
import { AttachmentFillType } from '../enums/attachmentFillType'
import { AttachmentFilterType } from '../enums/attachmentFiltertype'
import { fetchAttachments, fetchAttachmentsCount } from '../query-api'
import { getAttachmentsApiFilter } from '../utils/getAttachmentsApiFilter'

const COUNT_LIMIT = 999

const getLimitedValue = (value?: string | number): number => Math.min(Number(value), COUNT_LIMIT)

type CountValues = Record<AttachmentFilterType, number>
type OverLimitFlags = Record<AttachmentFilterType, boolean>

interface UseReceiptsCounterProps {
  enabled: boolean
}
interface UseReceiptsCounterResponse {
  countMap: CountValues
  hasReceivedEInvoices: boolean
  isOverLimitMap: OverLimitFlags
  isLoading: boolean
}

export const useAttachmentsCounter = (props?: UseReceiptsCounterProps): UseReceiptsCounterResponse => {
  const { enabled = true } = props || {}
  const { organization } = useUserOrganization()
  const isEInvoicingOnly = useIsEInvoicingOnly()
  const { eInvoiceFilter: eDocumentEInvoiceFilter } = getAttachmentsApiFilter(
    AttachmentFilterType.EDocument,
    false,
    isEInvoicingOnly,
  )

  const { data, isIdle, isLoading } = useQuery(
    [QueryKeys.AttachmentsCount, organization?.id],
    () => fetchAttachmentsCount(organization?.id || ''),
    {
      enabled: !!organization && enabled,
      staleTime: defaultStaleTime,
    },
  )

  const { data: blockedData, isLoading: isBlockedCountLoading } = useQuery(
    [QueryKeys.AttachmentsList, organization?.id, AttachmentFilterType.Blocked],
    () =>
      fetchAttachments({
        organizationId: organization?.id || '',
        type: AttachmentFilterType.EDocument,
        eInvoiceFilter: EInvoiceListFilter.Blocked,
        fillType: AttachmentFillType.Nonfilled,
        include: '',
        pageSize: 1,
      }),
    {
      enabled: !!organization && enabled,
      staleTime: defaultStaleTime,
    },
  )

  const { data: pendingEDocumentsData, isLoading: isPendingEDocumentsCountLoading } = useQuery(
    [QueryKeys.AttachmentsList, organization?.id, AttachmentFilterType.EDocument, eDocumentEInvoiceFilter],
    () =>
      fetchAttachments({
        organizationId: organization?.id || '',
        type: AttachmentFilterType.EDocument,
        eInvoiceFilter: eDocumentEInvoiceFilter,
        fillType: AttachmentFillType.Nonfilled,
        include: '',
        pageSize: 1,
      }),
    {
      enabled: !!organization && enabled,
      staleTime: defaultStaleTime,
    },
  )

  const { data: receivedEInvoicesData, isLoading: isReceivedEInvoicesCountLoading } = useQuery(
    [QueryKeys.AttachmentsList, organization?.id, AttachmentFilterType.EDocument],
    () =>
      fetchAttachments({
        organizationId: organization?.id || '',
        type: AttachmentFilterType.EDocument,
        include: '',
        pageSize: 1,
      }),
    {
      enabled: !!organization && enabled,
      staleTime: defaultStaleTime,
    },
  )

  const blockedCount = blockedData?.meta.paging.total ?? 0
  const pendingEDocumentsCount = pendingEDocumentsData?.meta.paging.total ?? 0
  const hasReceivedEInvoices = (receivedEInvoicesData?.meta.paging.total ?? 0) > 0

  const { counts } = data || {}
  const countLimitedValues: CountValues = useMemo(
    () => ({
      [AttachmentFilterType.All]: getLimitedValue(Number(counts?.unhandledAttachments ?? 0) - blockedCount),
      [AttachmentFilterType.Upload]: getLimitedValue(
        Number(counts?.unhandledAttachments ?? 0) -
          Number(counts?.unhandledEDocuments ?? 0) -
          blockedCount -
          Number(counts?.unhandledScoutReceipts ?? 0),
      ),
      // Matches the filter getAttachmentsApiFilter picks for the EDocument tab's own list,
      // so the badge excludes Blocked e-invoices (and Approved ones for e-invoicing-only orgs, which have their own tabs).
      [AttachmentFilterType.EDocument]: getLimitedValue(pendingEDocumentsCount),
      [AttachmentFilterType.Scout]: getLimitedValue(counts?.unhandledScoutReceipts ?? 0),
      [AttachmentFilterType.Blocked]: getLimitedValue(blockedCount),
      // Approved tab never shows a count badge (see UploadsTabs), so it isn't fetched.
      [AttachmentFilterType.Approved]: 0,
    }),
    [counts, blockedCount, pendingEDocumentsCount],
  )
  const isOverLimitMap: OverLimitFlags = useMemo(
    () => ({
      [AttachmentFilterType.All]: countLimitedValues[AttachmentFilterType.All] > COUNT_LIMIT,
      [AttachmentFilterType.Upload]: countLimitedValues[AttachmentFilterType.Upload] > COUNT_LIMIT,
      [AttachmentFilterType.EDocument]: countLimitedValues[AttachmentFilterType.EDocument] > COUNT_LIMIT,
      [AttachmentFilterType.Scout]: countLimitedValues[AttachmentFilterType.Scout] > COUNT_LIMIT,
      [AttachmentFilterType.Blocked]: countLimitedValues[AttachmentFilterType.Blocked] > COUNT_LIMIT,
      [AttachmentFilterType.Approved]: false,
    }),
    [countLimitedValues],
  )

  return {
    countMap: countLimitedValues,
    hasReceivedEInvoices,
    isOverLimitMap,
    isLoading:
      isIdle ||
      isLoading ||
      isBlockedCountLoading ||
      isPendingEDocumentsCountLoading ||
      isReceivedEInvoicesCountLoading,
  }
}
